<template>
<div v-resize='onResize'>
  <v-row wrap class='pa-2'>
    <v-col xs="12" class='pb-2'>
      <AppTitle icon='touch_app' title='Simple Interactive Simulator' />
    </v-col>
  </v-row>
  <v-row>
    <v-col cols="8" class='pr-2'>
      <v-row>
        <v-col v-for='(x, i) in visualItems' :key='i' xs="12" :class='{ "pb-3": !i }'>
          <v-card style='min-height: 300px; max-height: 300px;'>
            <div style='position:relative;' class='my-auto' :style='weatherColors[dayNightNum][weatherNum]'>
              <v-img :src='x.src' :lazy-src='x.src' max-height='300' min-height='300' :style='dayNightOpacity[dayNightNum]' style='position: relative;'>
                <template v-slot:placeholder>
                  <v-layout fill-height align-center justify-center class='ma-0'>
                    <v-progress-circular indeterminate color='primary' />
                  </v-layout>
                </template>
              </v-img>
              <div v-if='!i' id='waterEcharts' style='position: absolute;' :style='waterStyle' />
              <div :id='x.sun' class='innerChart' />
              <div :id='x.weather' class='innerChart' />
            </div>
          </v-card>
        </v-col>
      </v-row>
    </v-col>

    <v-col xs="4" class='pl-2'>
      <v-row wrap>
        <v-col xs="12" class='pb-2'>
          <v-card class='px-3'>
            <v-subheader class='pl-0'>Cistern Water Level ({{ waterLevelPerc }}%)</v-subheader>
            <v-slider v-model='waterLevelPerc' :thumb-size='24' thumb-label='always' append-icon='zoom_in' prepend-icon='zoom_out' @click:append='zoomIn' @click:prepend='zoomOut' />

            <v-subheader class='pl-0'>Solar Power Strengh ({{ solarPower }})</v-subheader>
            <v-slider v-model='solarPower' :thumb-size='24' thumb-label='always' append-icon='zoom_in' prepend-icon='zoom_out' @click:append='zoomInSolar' @click:prepend='zoomOutSolar' :max='9' />

            <v-row>
              <v-col xs="4" class='pr-2'>
                <v-tooltip bottom color='primary'>
                  <v-select v-model='solarTrajNum' :items='solarTrajectoryItems' label='Sunshine' slot='activator=on' />
                  <span>Select solar wave trajectory</span>
                </v-tooltip>
              </v-col>
              <v-col xs="4" class='px-2'>
                <v-tooltip bottom color='primary'>
                  <v-select v-model='dayNightNum' :items='dayNightItems' label='Day/Night' slot='activator=on' />
                  <span>Select day or night time</span>
                </v-tooltip>
              </v-col>
              <v-col xs="4" class='pl-2'>
                <v-tooltip bottom color='primary'>
                  <v-select v-model='weatherNum' :items='weatherItems' label='Weather' slot='activator=on' />
                  <span>Select weather condition</span>
                </v-tooltip>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row>
        <v-col xs="12" class='pt-2'>
          <v-card>
            <v-data-table :headers='headers' :items='tableItems' class='elevation-1 text-xs-center' hide-default-footer>
              <template v-slot:items='props'>
                <td class='text-xs-left'>{{ props.item.name }}</td>
                <td class='text-xs-left'>{{ props.item.amount }}</td>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</div>
</template>

<script>
import { symbol, solarTrajectory, rainTraj } from '@/components/subs/SubSimulator'
import AppTitle from '@/components/shared/Title'

export default {
  data: () => ({
    sunChart      : null,
    sunChart2     : null,
    solarPower    : 8,
    waterChart    : null,
    weatherNum    : 0,
    dayNightNum   : 0,
    solarTrajNum  : 0,
    weatherChart  : null,
    weatherChart2 : null,
    waterLevelPerc: 50,
    waterCollectionRate : 0,
    elecConsumptionRate : 90,
    waterConsumptionRate: 5,
    dayNightOpacity: ['opacity:0.8', 'opacity:0.4'],
    dayNightItems: [{ text: 'Day', value: 0 },   { text: 'Night', value: 1 }],
    weatherItems : [{ text: 'Shiny', value: 0 }, { text: 'Cloudy', value: 1 }, { text: 'Rainy', value: 2 }],
    solarTrajectoryItems: [{ text: 'Uniform', value: 0 }, { text: 'Radial', value: 1 }],
    weatherColors: [
      ['background-color:rgba(255,185,0,0.9)', 'background-color:rgba(20,20,155,1)', 'background-color:rgba(20,20,155,1)'],
      ['background-color:rgba(0,0,0,.9)', 'background-color:rgba(0,0,40,.9)', 'background-color:rgba(0,0,40,.9)'],
    ],

    headers: [
      { text: 'Metric', align: 'left', sortable: false, value: 'name' },
      { text: 'Amount', align: 'left', sortable: false, value: 'amount' },
    ],
    waterStyle: { top: '72.7%', left: '53.35%', width: '14%', height: '13.1%' },
  }),
  computed: {
    visualItems() {
      return [
        { src: require('../assets/LBC1.png'), sun: 'sunEcharts',  weather: 'weatherEcharts' },
        { src: require('../assets/LBC2.jpg'), sun: 'sunEcharts2', weather: 'weatherEcharts2' },
      ]
    },
    waterOption() {
      return {
        series: [{ type: 'bar', data: [this.waterLevelPerc] }]
      }
    },
    weatherOption() {
      this.solarPower = [8, 4, 1][this.weatherNum]
      this.waterCollectionRate = [0, 0, 10][this.weatherNum]
      return {
        series: rainTraj[this.weatherNum]
      }
    },
    sunOption() {
      return {
        series: [{
          name: 'tail',
          type: 'lines',
          coordinateSystem: 'cartesian2d',
          zlevel: 1,
          effect: { show: true, period: 10 - this.solarPower, trailLength: .8, color: '#ffa022', symbolSize: 3 },
          lineStyle: { normal: { color: '#ffa022', width: 0, curveness: 0.05 } },
          data: solarTrajectory[this.solarTrajNum]
        }, {
          name: 'sun',
          type: 'lines',
          coordinateSystem: 'cartesian2d',
          zlevel: 2,
          symbolSize: 25,
          effect: { show: true, period: 10 - this.solarPower, trailLength: 0, symbol, symbolSize: 15 },
          lineStyle: { normal: { color: '#ffa022', width: 0, opacity: 0.6, curveness: 0.05 } },
          data: solarTrajectory[this.solarTrajNum]
        }]
      }
    },
    tableItems() {
      return [{
        name: 'Cistern level',
        amount: `${ this.waterLevelPerc } %`,
      }, {
        name: 'No rain duration',
        amount: `${ _.round(this.waterLevelPerc / 2) } days`,
      }, {
        name: 'Water collection rate',
        amount: `${ this.waterCollectionRate } GPM`,
      }, {
        name: 'Water consumption rate',
        amount: `${ this.waterConsumptionRate } GPM`,
      }, {
        name: 'Energy collection rate',
        amount: `${ this.solarPower * 12 } Kw`,
      }, {
        name: 'Energy consumption rate',
        amount: `${ this.elecConsumptionRate } Kw`,
      }]
    },
  },
  methods: {
    zoomOut() {
      this.waterLevelPerc = _.max([this.waterLevelPerc - 10, 0])
    },
    zoomIn() {
      this.waterLevelPerc = _.min([this.waterLevelPerc + 10, 100])
    },
    zoomOutSolar() {
      this.solarPower = _.max([this.solarPower - 1, 0])
    },
    zoomInSolar() {
      this.solarPower = _.min([this.solarPower + 1, 9])
    },
    onResize() {
      if(!this.waterChart || !this.sunChart || !this.weatherChart) return
      _.debounce(() => {
        this.waterChart.resize()
        this.sunChart.resize()
        this.sunChart2.resize()
        this.weatherChart.resize()
        this.weatherChart2.resize()
      }, 100)()
    },
  },
  watch: {
    waterOption(to) {
      this.waterChart.setOption(to)
    },
    sunOption(to) {
      this.sunChart.setOption(to)
      this.sunChart2.setOption(to)
    },
    weatherOption(to) {
      this.weatherChart.setOption(to)
      this.weatherChart2.setOption(to)
    }
  },
  mounted() {
    const waterOption = {
      color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: 'white' }, { offset: 1, color: '#0095CC' }]),
      grid : { bottom: 0, left: 0, top: 0, right: 0 },
      xAxis: { data: ['Water'], show: false },
      yAxis: { show: false, min: 0, max: 100 },
      ...this.waterOption
    }
    const sunOption = {
      grid : { top: 5 },
      xAxis: { type: 'value', show: false, min: 0, max: 10 },
      yAxis: { min: 0, max: 10, show: false },
      ...this.sunOption
    }
    const weatherOption = {
      grid : { top: 0, left: 0 },
      xAxis: { type: 'value', show: false, min: 0, max: 10 },
      yAxis: { min: 0, max: 10, show: false },
      series: rainTraj[this.weatherNum],
    }

    this.waterChart    = echarts.init($('#waterEcharts'))
    this.sunChart      = echarts.init($('#sunEcharts'))
    this.sunChart2     = echarts.init($('#sunEcharts2'))
    this.weatherChart  = echarts.init($('#weatherEcharts'))
    this.weatherChart2 = echarts.init($('#weatherEcharts2'))

    this.waterChart.setOption(waterOption)
    this.sunChart.setOption(sunOption)
    this.sunChart2.setOption(sunOption)
    this.weatherChart.setOption(weatherOption)
    this.weatherChart2.setOption(weatherOption)

    //** eventBus
    this.$root.$on('updateWaterStyle', x => this.waterStyle = {
      top   : '72.7%',
      height: '13.1%',
      left  : !x ? '53.35%' : '54.35%',
      width : !x ? '14%'    : '17.7%',
    })
  },
  beforeDestroy() {
    echarts.dispose(this.waterChart)
    echarts.dispose(this.sunChart)
    echarts.dispose(this.sunChart2)
    echarts.dispose(this.weatherChart)
    echarts.dispose(this.weatherChart2)
  },
  components: {
    AppTitle
  }
}
</script>


<style lang='stylus' scoped>
table.v-table tbody td, table.v-table tbody th
  height: 41px !important

div.innerChart
  position: absolute
  top   : 0%
  left  : 0%
  width : 100%
  height: 100%
</style>

<style lang='stylus'>
table.v-table thead tr:not(.v-datatable__progress)
  height: 53px !important
</style>
